import { Box, Button, Flex, IconButton, Text, Textarea } from '@chakra-ui/react';
import { EditPencil, Trash } from 'iconoir-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ContactNote } from '../contact-types';

interface NoteCardProps {
  note: ContactNote;
  /** When provided, hover reveals an inline edit affordance. */
  onEdit?: (id: string, text: string) => void;
  /** When provided, hover reveals a delete affordance. */
  onDelete?: (id: string) => void;
}

/**
 * A single team note: initials avatar + author + timestamp header, then the body.
 * When `onEdit` / `onDelete` are passed, hover fades the timestamp and reveals
 * edit + delete controls; editing swaps the body for an inline textarea.
 *
 * The controls are absolutely positioned so revealing them never resizes the
 * card — the timestamp keeps its place in flow and only fades.
 */
const NoteCard: React.FC<NoteCardProps> = ({ note, onEdit, onDelete }) => {
  const { t } = useTranslation();
  const [isHovered, setIsHovered] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(note.text);

  const handleSave = () => {
    const text = draft.trim();
    if (text && text !== note.text) {
      onEdit?.(note.id, text);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setDraft(note.text);
    setIsEditing(false);
  };

  const hasActions = Boolean(onEdit || onDelete);
  const showActions = isHovered && !isEditing && hasActions;

  return (
    <Box
      position="relative"
      borderWidth="1px"
      borderColor="border.DEFAULT"
      rounded="10px"
      bg="babyPowder"
      px="11px"
      py="10px"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {hasActions && (
        <Flex
          position="absolute"
          top="10px"
          right="9px"
          zIndex="1"
          h="21px"
          align="center"
          gap="0.5"
          opacity={showActions ? 1 : 0}
          pointerEvents={showActions ? 'auto' : 'none'}
          transition="opacity 0.12s"
        >
          {onEdit && (
            <IconButton
              aria-label={t('contactDrawer.notes.edit')}
              size="2xs"
              variant="ghost"
              color="primary.400"
              onClick={() => {
                setDraft(note.text);
                setIsEditing(true);
              }}
            >
              <EditPencil width={13} height={13} />
            </IconButton>
          )}
          {onDelete && (
            <IconButton
              aria-label={t('contactDrawer.notes.delete')}
              size="2xs"
              variant="ghost"
              color="primary.400"
              _hover={{ color: 'red.500' }}
              onClick={() => onDelete(note.id)}
            >
              <Trash width={13} height={13} />
            </IconButton>
          )}
        </Flex>
      )}

      <Flex align="center" gap="7px" mb="6px">
        <Box
          display="grid"
          placeItems="center"
          boxSize="21px"
          flex="none"
          rounded="full"
          bg="primary.100"
          color="primary.600"
          fontFamily="heading"
          fontSize="9px"
          fontWeight="bold"
        >
          {note.initials}
        </Box>
        <Text fontFamily="heading" fontSize="11.5px" fontWeight="semibold" color="dark">
          {note.author}
        </Text>
        <Text
          ml="auto"
          fontFamily="body"
          fontSize="10.5px"
          color="inkSubtle"
          opacity={showActions ? 0 : 1}
          transition="opacity 0.12s"
        >
          {note.date}
        </Text>
      </Flex>

      {isEditing ? (
        <Box>
          <Textarea
            variant="outline"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            autoFocus
            rounded="md"
            minH="54px"
            bg="panelBg"
            fontFamily="body"
            fontSize="12.5px"
            lineHeight="1.5"
          />
          <Flex justify="flex-end" gap="2" mt="2">
            <Button size="xs" variant="ghost" color="primary.500" onClick={handleCancel}>
              {t('contactDrawer.notes.cancel')}
            </Button>
            <Button
              size="xs"
              bg="dark"
              color="white"
              _hover={{ bg: 'primary.800' }}
              disabled={!draft.trim() || draft.trim() === note.text}
              onClick={handleSave}
            >
              {t('contactDrawer.notes.save')}
            </Button>
          </Flex>
        </Box>
      ) : (
        <Text fontFamily="body" fontSize="12.5px" color="primary.600" lineHeight="1.5">
          {note.text}
        </Text>
      )}
    </Box>
  );
};

export default NoteCard;
